import { useEffect, useMemo, useState, type CSSProperties } from "react";

import type { MapNode } from "@thalamos/aspire-common";

import { useIsMobile } from "../hooks/useIsMobile.js";
import type { NodeTypeVisibility } from "../types.js";

type TimeWindow = "7d" | "30d" | "90d" | "1y" | "all";

const TIME_OPTIONS: { value: TimeWindow; label: string }[] = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "1y", label: "1 year" },
  { value: "all", label: "All time" },
];

const NODE_TYPE_CONFIG: {
  type: MapNode["type"];
  label: string;
  colour: string;
}[] = [
  { type: "nhs-trust", label: "NHS Trusts", colour: "#6366f1" },
  { type: "nhs-hospital", label: "NHS Hospitals", colour: "#38bdf8" },
  { type: "local-authority", label: "Local Authorities", colour: "#14b8a6" },
  {
    type: "police",
    label: "Police Units",
    colour: "#22c55e",
  },
  { type: "place-of-safety", label: "Places of Safety", colour: "#f59e0b" },
  { type: "cqc", label: "CQC", colour: "#a855f7" },
  { type: "cqc-provider", label: "CQC Providers", colour: "#ec4899" },
  { type: "independent", label: "Independent Doctors", colour: "#8b5cf6" },
  { type: "prison", label: "Prisons", colour: "#64748b" },
  {
    type: "incident-location",
    label: "Incident Locations",
    colour: "#ef4444",
  },
  { type: "gp-practice", label: "GP Practices", colour: "#10b981" },
  { type: "pcn", label: "Primary Care Networks", colour: "#06b6d4" },
];

const EDGE_LEGEND = [
  { label: "Work routing (Aspire)", colour: "rgba(59, 130, 246, 0.6)" },
  { label: "Incident share (Vision)", colour: "rgba(34, 197, 94, 0.6)" },
  { label: "POS arrival (Vision)", colour: "rgba(245, 158, 11, 0.6)" },
  {
    label: "Incident location (Vision)",
    colour: "rgba(239, 68, 68, 0.6)",
  },
  {
    label: "CQC share",
    colour: "rgba(236, 72, 153, 0.6)",
  },
  {
    label: "Independent share",
    colour: "rgba(139, 92, 246, 0.6)",
  },
  {
    label: "POS transfer",
    colour: "rgba(251, 191, 36, 0.6)",
  },
  {
    label: "Admission → GP",
    colour: "rgba(16, 185, 129, 0.6)",
  },
  {
    label: "Admission → PCN",
    colour: "rgba(6, 182, 212, 0.6)",
  },
  {
    label: "GP → PCN",
    colour: "rgba(6, 182, 212, 0.3)",
  },
];

interface MapControlsProps {
  timeWindow: TimeWindow;
  onTimeWindowChange: (tw: TimeWindow) => void;
  visibility: NodeTypeVisibility;
  onVisibilityChange: (v: NodeTypeVisibility) => void;
  nodeCount: number;
  edgeCount: number;
  allNodes: MapNode[];
  trustFilter: string | null;
  onTrustFilterChange: (id: string | null) => void;
  onExpandedChange?: (expanded: boolean) => void;
}

const panelStyle: CSSProperties = {
  position: "absolute",
  bottom: 24,
  left: 24,
  background: "rgba(15, 23, 42, 0.92)",
  backdropFilter: "blur(12px)",
  border: "1px solid rgba(148, 163, 184, 0.15)",
  borderRadius: 12,
  padding: 16,
  color: "#e2e8f0",
  fontSize: 13,
  zIndex: 50,
  minWidth: 220,
  maxWidth: "calc(100vw - 48px)",
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.4)",
};

const sectionTitle: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
  color: "#94a3b8",
  marginBottom: 8,
  marginTop: 12,
};

const buttonBase: CSSProperties = {
  padding: "4px 10px",
  border: "1px solid rgba(148, 163, 184, 0.2)",
  borderRadius: 6,
  background: "transparent",
  color: "#94a3b8",
  fontSize: 12,
  cursor: "pointer",
  transition: "all 0.15s",
};

const buttonActive: CSSProperties = {
  ...buttonBase,
  background: "rgba(59, 130, 246, 0.2)",
  borderColor: "#3b82f6",
  color: "#e2e8f0",
};

const toggleButtonStyle: CSSProperties = {
  position: "absolute",
  bottom: 24,
  left: 24,
  width: 44,
  height: 44,
  borderRadius: 12,
  border: "1px solid rgba(148, 163, 184, 0.15)",
  background: "rgba(15, 23, 42, 0.92)",
  backdropFilter: "blur(12px)",
  color: "#e2e8f0",
  fontSize: 20,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 50,
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.4)",
};

function ControlsPanel({
  timeWindow,
  onTimeWindowChange,
  visibility,
  onVisibilityChange,
  nodeCount,
  edgeCount,
  allNodes,
  trustFilter,
  onTrustFilterChange,
  onClose,
  fullscreen,
}: MapControlsProps & { onClose?: () => void; fullscreen?: boolean }) {
  const trustOptions = useMemo(
    () =>
      allNodes
        .filter((n) => n.type === "nhs-trust")
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allNodes],
  );
  const style: CSSProperties = fullscreen
    ? {
        ...panelStyle,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: 0,
        border: "none",
        minWidth: 0,
        maxWidth: "none",
        padding: 16,
        maxHeight: "100dvh",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }
    : panelStyle;

  return (
    <div style={style}>
      {/* Header row */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          paddingLeft: fullscreen ? 36 : 0,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: "0.02em",
              marginBottom: 4,
            }}
          >
            Organisation Network
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8 }}>
            {nodeCount} organisations · {edgeCount} connections
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#64748b",
              fontSize: 18,
              cursor: "pointer",
              padding: "0 0 0 8px",
              lineHeight: 1,
            }}
            aria-label="Close controls"
          >
            ✕
          </button>
        )}
      </div>

      {/* Trust filter */}
      {trustOptions.length > 0 && (
        <>
          <div style={sectionTitle}>Filter by Trust</div>
          <select
            value={trustFilter ?? ""}
            onChange={(e) => onTrustFilterChange(e.target.value || null)}
            style={{
              width: "100%",
              height: 36,
              padding: "0 8px",
              borderRadius: 6,
              border: "1px solid rgba(148, 163, 184, 0.2)",
              background: "rgba(15, 23, 42, 0.8)",
              color: "#e2e8f0",
              fontSize: 13,
              lineHeight: "36px",
              cursor: "pointer",
              boxSizing: "border-box",
            }}
          >
            <option value="">All trusts</option>
            {trustOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </>
      )}

      {/* Time window */}
      <div style={sectionTitle}>Time Range</div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {TIME_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onTimeWindowChange(opt.value)}
            style={timeWindow === opt.value ? buttonActive : buttonBase}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Node type filters */}
      <div style={sectionTitle}>Organisations</div>
      {NODE_TYPE_CONFIG.map(({ type, label, colour }) => (
        <label
          key={type}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 4,
            cursor: "pointer",
            fontSize: 12,
          }}
        >
          <input
            type="checkbox"
            checked={visibility[type]}
            onChange={() =>
              onVisibilityChange({
                ...visibility,
                [type]: !visibility[type],
              })
            }
            style={{ accentColor: colour }}
          />
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: colour,
              display: "inline-block",
              boxShadow: `0 0 6px ${colour}60`,
            }}
          />
          {label}
        </label>
      ))}

      {/* Edge legend */}
      <div style={sectionTitle}>Connections</div>
      {EDGE_LEGEND.map(({ label, colour }) => (
        <div
          key={label}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 3,
            fontSize: 12,
          }}
        >
          <div
            style={{
              width: 20,
              height: 3,
              background: colour,
              borderRadius: 2,
            }}
          />
          <span style={{ color: "#94a3b8" }}>{label}</span>
        </div>
      ))}

      {/* Flash indicator */}
      <div
        style={{
          marginTop: 12,
          fontSize: 11,
          color: "#64748b",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <style>{`
          @keyframes network-map-pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
        `}</style>
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "#ffffff",
            boxShadow: "0 0 8px #ffffff80",
            display: "inline-block",
            animation: "network-map-pulse 2s infinite",
          }}
        />
        Neuron flashes: replay mode
      </div>
    </div>
  );
}

export function MapControls(props: MapControlsProps) {
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(!isMobile);

  // Collapse when switching to mobile, expand when switching to desktop
  useEffect(() => {
    setExpanded(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    props.onExpandedChange?.(expanded);
  }, [expanded, props.onExpandedChange]);

  if (!expanded) {
    return (
      <button
        style={toggleButtonStyle}
        onClick={() => setExpanded(true)}
        aria-label="Open controls"
      >
        {/* Layers icon (three stacked diamonds) */}
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polygon points="12 2 2 7 12 12 22 7 12 2" />
          <polyline points="2 17 12 22 22 17" />
          <polyline points="2 12 12 17 22 12" />
        </svg>
      </button>
    );
  }

  return (
    <ControlsPanel
      {...props}
      onClose={() => setExpanded(false)}
      fullscreen={isMobile}
    />
  );
}
